<template>
  <wdw-dynbox
    :title="filterTitle"
    :config="{ showControls: true }"
    :minimize="true"
    ref="wdwDynboxRef"
    :watermark="watermark"
    @toggled-minimize="onFilterToggled"
  >
    <template v-if="filterApplied" #dynamicTitle>
      <font-awesome-icon icon="filter" />
    </template>
    <template #default>
      <div>
        <Form @submit="applyFilter">
          <div class="wdw-form" id="wdw-filter-form">
            <div v-for="field in fieldsArray" v-bind:key="field.filter_name">
              <div
                v-if="field.type == 'number' || field.type == 'text' || field.type == 'email' || field.type == 'link'"
              >
                <form-control-input
                  :additionalLabelClasses="'wdw-filter-label'"
                  :label="field.name"
                  :type="field.type"
                  :name="field.filter_name"
                  :required="false"
                  :public="true"
                  :hidden="!field.visible"
                  :reload="filterResetCount"
                  :defaultvalue="defaultFilterValues[field.filter_name] + ''"
                  :overridevalue="initialFilterValueFromQuery[field.filter_name] + '' || ''"
                />
              </div>

              <div v-if="field.type == 'valuelist'">
                <form-control-select
                  :additionalLabelClasses="'wdw-filter-label'"
                  :label="field.name"
                  :name="field.filter_name"
                  :required="false"
                  :public="true"
                  :options="field.values.sort((a, b) => a.text - b.text)"
                  :multiselect="field.multiselect"
                  :defaultvalue="toArray(defaultFilterValues[field.filter_name] + '')"
                  :shouldcache="false"
                  :reload="filterResetCount"
                  :allowClear="field.allowClear ? field.allowClear : false"
                  :overridevalue="
                    initialFilterValueFromQuery[field.filter_name]
                      ? toArray(initialFilterValueFromQuery[field.filter_name] || '')
                      : []
                  "
                  @selected="selectedElem($event, field)"
                />
              </div>
            </div>
          </div>
          <div>
            <fieldset v-if="showAttributeFields.length > 0">
              <form-control-search-attributes
                :resetTrigger="filterResetCount"
                :fields="showAttributeFields"
                :overrideDefaultValues="defaultFilterValues"
              />
            </fieldset>
          </div>
          <button class="btn btn-success btn-form-submit btn-submit-filter" type="submit">
            <font-awesome-icon icon="filter" />
            {{ domainStore.translate('discuss.filter.submit') }}
          </button>
          &nbsp;
          <button class="btn btn-danger btn-reset-filter" @click="resetFilter">
            <font-awesome-icon icon="trash-alt" />
            {{ domainStore.translate('discuss.filter.reset') }}
          </button>
        </Form>
      </div>
    </template>
  </wdw-dynbox>
</template>

<script>
import { thoughtStore } from '@/store/thought-store';
import { projectStore } from '@/store/project-store';
import useHashParams from '@/composables/general/useHashParams';
import FormControlInput from '@/components/general/FormControl/Input';
import FormControlSelect from '@/components/general/FormControl/Select';
import FormControlSearchAttributes from '@/components/general/FormControl/SearchAttributes';
import useConverter from '@/composables/general/useConverter';
import { toRef, ref, unref, computed, watch, onBeforeMount, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import WdwDynbox from '@/components/general/WdwDynbox.vue';
import { Form } from 'vee-validate';
import { filterStore } from '@/store/filter-store';
import { domainStore } from '@/store/domain-store';
// import { projectStore } from '@/store/project-store';
export default {
  name: 'ThoughtFilter',
  props: {
    fields: {
      type: Object,
      required: true,
    },
    attributeFields: {
      type: Array,
      required: true,
    },
    filterAttributesDependency: {
      type: Object,
      required: false,
    },
    refreshList: {
      type: Number,
      default: 0,
    },
    watermark: {
      type: Boolean,
      default: false,
      required: false,
    },
    // If set to true, topicid will not be removed when filter is reset
    // Useful if we are binding this filter to Topic-widget
    staticTopic: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  emits: ['on-filter-toggled'],

  setup(props, context) {
    const route = useRoute();
    const { getHashParams } = useHashParams(route);
    const { toArray } = useConverter();
    const wdwDynboxRef = ref();
    const refreshList = toRef(props, 'refreshList');
    const fields = toRef(props, 'fields');
    const attributeFields = toRef(props, 'attributeFields');
    const filterResetCount = ref(0);
    const showAttributeFields = ref(props.attributeFields);
    const initialFilterValueFromQuery = ref({});
    const attributeFilters = ref({});

    // computed
    const fieldsArray = computed(() => {
      const fieldsArr = [];
      for (const [key, value] of Object.entries(fields.value)) {
        value.filter_name = key;
        fieldsArr.push(value);
      }
      return fieldsArr;
    });
    const attributeFieldsArray = computed(() => {
      const fieldsarr = [];
      for (const [key, value] of Object.entries(attributeFields.value)) {
        fieldsarr.push(value);
      }
      filterStore.setAttributeFields(fieldsarr);
      filterStore.setFields(fieldsArray.value.concat(fieldsarr));
      return fieldsarr;
    });
    const idk = computed(() => {
      return filterStore.getState();
    });
    const defaultFilterValues = computed(() => {
      const defaultValues = {};
      // T22381: defaultvalues should not retrieve anything from hash params except for topic_id
      //        That is reserved for setThoughtFilter, which is called from the start or when route changes
      const hashParamValues = getHashParams();

      // topic_id can be parameter as well (especially when called from backend)

      if (hashParamValues.topic_id) {
        hashParamValues.topicid = hashParamValues.topic_id;
      }

      // T18783: Assign default sort from field
      if (fields.value && fields.value.sort) {
        defaultValues['sort'] = fields.value.sort.default_value;
      }

      for (const [key, value] of Object.entries(fields.value)) {
        if (thoughtStore.isValidThoughtFilter(key)) {
          if (value.default_value) {
            defaultValues[key] = value.default_value;
          } else {
            defaultValues[key] = '';
          }
        }
      }

      attributeFields.value.forEach(el => {
        if (thoughtStore.isValidThoughtFilter(el.filter_name)) {
          defaultValues[el.filter_name] = '';
        }
      });

      filterStore.setDefaultValues(defaultValues);
      return defaultValues;
    });
    const filterApplied = computed(() => {
      return JSON.stringify(thoughtStore.getState().thoughtFilter) !== JSON.stringify(defaultFilterValues.value);
    });
    const project = computed(() => {
      return projectStore.getState().project;
    });
    const filterTitle = computed(() => {
      if (domainStore.getState().language == 'de' && project.value?.config?.thought_filter_title) {
        return project.value?.config.thought_filter_title;
      }
      return domainStore.translate('discuss.filter.title');
    });

    //methods
    // update filter entries (i.e. when route.query changes)
    function updateShowAttributeFields(value) {
      showAttributeFields.value.forEach(showAttributeField => {
        const valueArray = value[showAttributeField.filter_name].split(',');
        showAttributeField.default_value = valueArray;
      });
    }
    function applyFilter(values) {
      // T18783: sort param should not be of array
      if (Array.isArray(values.sort) && values.sort.length > 0) {
        values.sort = values.sort[0];
      }
      // update filter
      // refresh=true (already by default) so route.query is updated (Problem on T18377)
      filterStore.setSelectedFilter(values, route);
      // toggle Minimize (collapes filter again)
      wdwDynboxRef.value.toggleMinimize();
    }
    function resetFilter(e) {
      // T18783: Reset topicid, otherwise user cannot clear filter form completely due to topic_id being set in hashparameter
      if (!props.staticTopic) {
        delete defaultFilterValues.value['topicid'];
      }
      // get filter object copy, and update hash params
      const elCopy = JSON.parse(JSON.stringify(defaultFilterValues.value));
      // Reset the filter
      filterStore.resetFilter(elCopy, route);
      // toggle Minimize (collapes filter again)
      wdwDynboxRef.value.toggleMinimize();
      // use custom key to force rerender (so as to reload form with default values)
      filterResetCount.value += 1;
      // prevent triggering submit on form
      e.preventDefault();
    }

    function selectedElem(event) {
      // check prop and change attributes in case of dependencies
      // used in MapProject component, to show attributes belonging to selected topic (if specific topic is selected)
      if (props.filterAttributesDependency) {
        if (event.field && props.filterAttributesDependency[event.field]) {
          if (
            event.value &&
            props.filterAttributesDependency[event.field][event.value] &&
            props.filterAttributesDependency[event.field][event.value].length > 0
          ) {
            showAttributeFields.value = props.filterAttributesDependency[event.field][event.value];
          } else {
            // if no specific dependencies, reset to provided props (all available attributes)
            showAttributeFields.value = attributeFields.value;
          }
        }
      }
    }

    function setThoughtFilter() {
      // get filter object copy, and update hash params
      const initialFilterValues = JSON.parse(JSON.stringify(defaultFilterValues.value));
      // hash params
      const hashParamValues = getHashParams();
      // update route query filters detected for initial filter on start of component
      for (const [key, value] of Object.entries(route.query)) {
        if (thoughtStore.isValidThoughtFilter(key) && value) {
          const valueFromHash = hashParamValues[key];
          initialFilterValues[key] = valueFromHash ? valueFromHash : '';
        }
      }
      initialFilterValueFromQuery.value = initialFilterValues;

      // update Select FormControl default_values if clicked on a district in WdwQuarterMap.vue
      updateShowAttributeFields(initialFilterValues);
      // initiate with filter values gathered on startup
      thoughtStore.setThoughtFilter(initialFilterValues);
    }

    function onFilterToggled(isMinimized) {
      context.emit('on-filter-toggled', isMinimized);
    }

    onMounted(() => {
      filterStore.setDefaultFields(fieldsArray.value);
      /**
       * T22722: If you don't want this to be called every time, use v-show instead of v-if
       */
      setThoughtFilter();
    });
    // reset Thoughtfilter and Thoughtlist to defaultValues
    watch(refreshList, (newVal, oldVal) => {
      if (newVal !== oldVal && newVal !== 0) {
        thoughtStore.reloadThoughts();
      }
    });
    // update Thoughtfilter and Thoughtlist when route.query changes (i.e. WdwQuarterMap)
    watch(
      () => route.query,
      () => {
        /**
         * Note: This only triggers when the hash parameter is set by setting the location hash directly,
         *    In other words, calling `useHashParams.setHashParams` with refresh=true
         */
        const hashKeys = Object.keys(route.query);
        if ((hashKeys.length == 1 && hashKeys[0] == 'thought_id') || hashKeys.length == 0) {
          /**
           *    T22592:
           *      Don't trigger filter if it's going to detail view of thought.
           *      The user is going to detail view
           *
           *    T22722:
           *      in Topic.vue, going back from detail view to list will give empty object
           * */
        } else {
          setThoughtFilter();
        }
      }
    );

    return {
      // refs
      domainStore,
      wdwDynboxRef,
      showAttributeFields,
      idk,
      filterResetCount,
      initialFilterValueFromQuery,
      //computed
      fieldsArray,
      filterApplied,
      defaultFilterValues,
      attributeFieldsArray,
      filterTitle,
      // methods
      applyFilter,
      toArray,
      resetFilter,
      selectedElem,
      onFilterToggled,
    };
  },

  components: {
    FormControlInput,
    FormControlSelect,
    FormControlSearchAttributes,
    WdwDynbox,
    Form,
  },
};
</script>

<style scoped></style>
